import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface TrainingRecord {
  id: string
  staff_name: string
  role: string | null
  training_type: string
  training_date: string
  trainer_provider: string | null
  expiry_date: string | null
  notes: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export const isExpired = (record: TrainingRecord): boolean => {
  if (!record.expiry_date) return false
  return new Date(record.expiry_date) < new Date()
}

export const isExpiringSoon = (record: TrainingRecord): boolean => {
  if (!record.expiry_date || isExpired(record)) return false
  const expiryTime = new Date(record.expiry_date).getTime()
  const nowTime = Date.now()
  const daysRemaining = (expiryTime - nowTime) / (1000 * 60 * 60 * 24)
  return daysRemaining <= 30
}

export function useETSFTraining() {
  const [training, setTraining] = useState<TrainingRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadTraining = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: qErr } = await supabase
        .from('etsf_training_records')
        .select('*')
        .order('training_date', { ascending: false })

      if (qErr) throw qErr
      setTraining((data || []) as TrainingRecord[])
    } catch (e: any) {
      setError(e.message || 'Failed to load training records')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTraining()
  }, [loadTraining])

  const createTraining = useCallback(
    async (values: Partial<TrainingRecord>) => {
      const { data, error } = await supabase
        .from('etsf_training_records')
        .insert([values])
        .select()
        .single()
      if (error) throw error
      await loadTraining()
      return data as TrainingRecord
    },
    [loadTraining]
  )

  const updateTraining = useCallback(
    async (id: string, values: Partial<TrainingRecord>) => {
      const { data, error } = await supabase
        .from('etsf_training_records')
        .update({ ...values, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      await loadTraining()
      return data as TrainingRecord
    },
    [loadTraining]
  )

  const deleteTraining = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from('etsf_training_records')
        .delete()
        .eq('id', id)
      if (error) throw error
      await loadTraining()
    },
    [loadTraining]
  )

  return {
    training,
    loading,
    error,
    reload: loadTraining,
    createTraining,
    updateTraining,
    deleteTraining,
  }
}
